import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { dateFromSearch } from '../../components/DateFields/DateFields';
import { LANGUAGE_ANSWER_OPTIONS } from '../../data/siteLanguages';
import {
  EmText, LinkedNote, chartDirection, chartHref, fill, useChartCopy, useChartLanguage,
} from '../../data/chartLanguage';
import { askChart, drawChart, redrawHouses, searchPlaces } from '../../services/chartService';
import { writeSavedChart } from '../../services/savedChart';
import BigThree from './BigThree';
import ChartNav from './ChartNav';
import ChartShare from './ChartShare';
import ChartTarot from './ChartTarot';
import { wrapReading } from './ReadingText';
import './BirthChart.css';

const READING_LANGUAGE_OPTIONS = LANGUAGE_ANSWER_OPTIONS.filter((option) => option.value);

const emptyForm = () => ({
  name: '',
  placeQuery: '',
  month: '',
  day: '',
  year: '',
  hour: '',
  minute: '',
  ampm: 'AM',
  timeUnknown: false,
});

function toHour24(hour, ampm) {
  const value = Number(hour);
  if (ampm === 'AM') return value === 12 ? 0 : value;
  return value === 12 ? 12 : value + 12;
}

const INTROS = {
  chart: {
    eyebrow: 'Tool · Free',
    title: <>Your birth chart, <em>precisely</em> drawn.</>,
    lede: 'Date, exact time, and place. We draw the chart, write a full reading, and you can keep asking about it.',
    button: 'Draw my chart',
  },
  'big-three': {
    eyebrow: 'Big Three calculator · Free',
    title: <>Your Sun, Moon, and <em>Rising</em>.</>,
    lede: 'The three placements that say the most about you: who you are, what you need, and how you come across. Add your birth time and place for an exact rising sign.',
    button: 'Find my Big Three',
  },
  rising: {
    eyebrow: 'Rising sign calculator · Free',
    title: <>Find your <em>rising</em> sign.</>,
    lede: 'Your rising sign, or ascendant, changes about every two hours, so it needs your birth time and the city you were born in. You also get your full chart and reading.',
    button: 'Find my rising sign',
  },
  moon: {
    eyebrow: 'Moon sign calculator · Free',
    title: <>Find your <em>moon</em> sign.</>,
    lede: 'Your moon sign describes what you need to feel safe and loved. A birth time makes it exact; without one, we tell you if the Moon changed signs that day.',
    button: 'Find my moon sign',
  },
};

const BirthChartTool = ({ embedded = false, onDrawnChange, variant = 'chart' }) => {
  const location = useLocation();
  const { language, chooseLanguage } = useChartLanguage();
  const copy = useChartCopy(language);
  const text = copy.form;
  const drawn = copy.result;
  const houses = [
    { id: 'P', label: drawn.placidus },
    { id: 'W', label: drawn.wholeSign },
    { id: 'A', label: drawn.equal },
  ];
  const intro = variant === 'chart' ? {
    eyebrow: copy.chart.eyebrow,
    title: <EmText text={copy.chart.title} />,
    lede: copy.chart.lede,
    button: copy.chart.button,
  } : (INTROS[variant] || INTROS.chart);
  const direction = chartDirection(language);
  const [form, setForm] = useState(emptyForm);
  const [places, setPlaces] = useState([]);
  const [placeOpen, setPlaceOpen] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [searching, setSearching] = useState(false);
  const [phase, setPhase] = useState('form');
  const [chart, setChart] = useState(null);
  const [reading, setReading] = useState('');
  const [error, setError] = useState('');
  const [readingError, setReadingError] = useState('');
  const [houseBusy, setHouseBusy] = useState(false);
  const [payload, setPayload] = useState(null);
  const [readingHouse, setReadingHouse] = useState(null);
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [tarotDrawn, setTarotDrawn] = useState(false);
  const navItems = [
    { id: 'chart-big-three', label: drawn.navBigThree },
    { id: 'chart-reading', label: drawn.navChart },
    ...(session ? [{ id: 'chart-ask', label: drawn.navAsk }] : []),
    { id: 'chart-tarot', label: drawn.navTarot },
    { id: 'chart-share', label: drawn.navShare },
    { id: 'chart-compare', label: drawn.navCompare },
    { id: 'chart-today', label: drawn.navToday || 'Today' },
  ];
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const abortRef = useRef(null);
  const askAbortRef = useRef(null);
  const placeRef = useRef(null);
  const detailsRef = useRef(null);

  const onDrawnRef = useRef(onDrawnChange);
  onDrawnRef.current = onDrawnChange;

  useEffect(() => {
    onDrawnRef.current?.(phase !== 'form');
  }, [phase]);

  useEffect(() => {
    const fromUrl = dateFromSearch(location.search);
    if (!fromUrl) return;
    setForm((current) => ({ ...current, month: fromUrl.month, day: fromUrl.day, year: fromUrl.year }));
  }, [location.search]);

  useEffect(() => () => {
    abortRef.current?.abort();
    askAbortRef.current?.abort();
  }, []);

  const clearAnswers = () => {
    askAbortRef.current?.abort();
    setSession(null);
    setAnswers([]);
    setTarotDrawn(false);
    setQuestion('');
    setAsking(false);
  };

  useEffect(() => {
    if (selectedPlace || form.placeQuery.trim().length < 2) {
      setPlaces([]);
      setSearching(false);
      return undefined;
    }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const rows = await searchPlaces(form.placeQuery.trim());
        setPlaces(rows);
        setPlaceOpen(true);
      } catch (err) {
        setPlaces([]);
        setError(err.message || text.placeSearchError);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [form.placeQuery, selectedPlace]);

  useEffect(() => {
    const close = (event) => {
      if (placeRef.current && !placeRef.current.contains(event.target)) {
        setPlaceOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const setField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setError('');
  };

  const buildPayload = () => {
    const month = Number(form.month);
    const day = Number(form.day);
    const year = Number(form.year);
    const born = new Date(year, month - 1, day);
    const realDate = born.getFullYear() === year && born.getMonth() === month - 1 && born.getDate() === day;
    if (!form.month || !form.day || !form.year || !realDate) {
      return text.dateError;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (year < 1900 || born > today) {
      return text.yearError;
    }
    if (!selectedPlace) {
      return text.placeError;
    }
    let hour = null;
    let minute = null;
    if (!form.timeUnknown) {
      hour = Number(form.hour);
      minute = Number(form.minute);
      if (
        form.hour === '' || form.minute === ''
        || Number.isNaN(hour) || Number.isNaN(minute)
        || hour < 1 || hour > 12 || minute < 0 || minute > 59
      ) {
        return text.timeError;
      }
      hour = toHour24(hour, form.ampm);
    }
    return {
      name: form.name.trim(),
      year,
      month,
      day,
      hour,
      minute: form.timeUnknown ? null : minute,
      time_unknown: form.timeUnknown,
      latitude: selectedPlace.latitude,
      longitude: selectedPlace.longitude,
      timezone: selectedPlace.timezone,
      place_label: selectedPlace.label,
      house_system: 'P',
      language,
      tool: variant === 'chart' ? 'birth-chart' : variant,
    };
  };

  const handleDraw = async (event) => {
    event.preventDefault();
    const next = buildPayload();
    if (typeof next === 'string') {
      setError(next);
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setError('');
    setReadingError('');
    setReading('');
    clearAnswers();
    setChart(null);
    setPayload(next);
    setReadingHouse(next.house_system);
    setPhase('drawing');
    let gotChart = false;
    await drawChart(next, {
      signal: controller.signal,
      onChart: (data) => {
        gotChart = true;
        writeSavedChart(next);
        setChart(data);
        setPhase('drawn');
      },
      onToken: (token) => setReading((current) => current + token),
      onDone: (data) => {
        setPhase(gotChart ? 'drawn' : 'form');
        if (data?.session) setSession(data.session);
      },
      onError: (message) => {
        if (gotChart) setReadingError(message);
        else {
          setPhase('form');
          setError(message);
        }
      },
    });
  };

  const handleHouse = async (houseSystem) => {
    if (!payload || houseBusy || chart?.house_system === houseSystem) return;
    setHouseBusy(true);
    setReadingError('');
    try {
      const next = { ...payload, house_system: houseSystem };
      const data = await redrawHouses(next);
      setPayload(next);
      setChart((current) => ({ ...current, ...data }));
    } catch (err) {
      setReadingError(err.message || drawn.housesFailed);
    } finally {
      setHouseBusy(false);
    }
  };

  const handleEdit = () => {
    abortRef.current?.abort();
    clearAnswers();
    setPhase('form');
    setChart(null);
    setReading('');
    setReadingError('');
  };

  const handleReset = () => {
    abortRef.current?.abort();
    clearAnswers();
    setForm(emptyForm());
    setSelectedPlace(null);
    setPlaces([]);
    setChart(null);
    setReading('');
    setPayload(null);
    setError('');
    setReadingError('');
    setPhase('form');
  };

  const retryReading = () => {
    if (!payload) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setReading('');
    setReadingError('');
    clearAnswers();
    setReadingHouse(payload.house_system);
    setPhase('drawing');
    drawChart(payload, {
      signal: controller.signal,
      onChart: (data) => {
        setChart(data);
        setPhase('drawn');
      },
      onToken: (token) => setReading((current) => current + token),
      onDone: (data) => {
        setPhase('drawn');
        if (data?.session) setSession(data.session);
      },
      onError: (message) => {
        setPhase('drawn');
        setReadingError(message);
      },
    });
  };

  const handleAsk = (event) => {
    event.preventDefault();
    const text = question.trim();
    if (!session || asking || !text) return;
    const controller = new AbortController();
    askAbortRef.current = controller;
    const index = answers.length;
    const update = (change) => setAnswers((current) => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, ...change(item) } : item
    )));
    setAnswers((current) => [...current, { question: text, answer: '', error: '' }]);
    setQuestion('');
    setAsking(true);
    askChart(session, text, {
      signal: controller.signal,
      onToken: (token) => update((item) => ({ answer: item.answer + token })),
      onDone: () => setAsking(false),
      onError: (message) => {
        update(() => ({ error: message }));
        setAsking(false);
      },
    });
  };

  const openDetails = () => detailsRef.current?.showModal?.();
  const closeDetails = () => detailsRef.current?.close?.();

  return (
    <div className={`birth-tool${embedded ? ' embedded' : ''}`}>
      {phase === 'form' ? (
        <>
          {!embedded && (
            <header className="birth-intro">
              <p className="birth-eyebrow">{intro.eyebrow}</p>
              <h1>{intro.title}</h1>
              <p className="birth-lede">{intro.lede}</p>
            </header>
          )}
          <form className="birth-form" onSubmit={handleDraw} {...direction}>
            <label>
              {text.name}
              <input
                value={form.name}
                maxLength={80}
                placeholder={text.optional}
                onChange={(event) => setField('name', event.target.value)}
              />
            </label>
            <div className="birth-place" ref={placeRef}>
              <label className="birth-place-label">
                {text.place}
                <input
                  value={form.placeQuery}
                  autoComplete="off"
                  placeholder={text.placePlaceholder}
                  onChange={(event) => {
                    setSelectedPlace(null);
                    setField('placeQuery', event.target.value);
                    setPlaceOpen(true);
                  }}
                  onFocus={() => places.length && setPlaceOpen(true)}
                />
              </label>
              {placeOpen && (places.length > 0 || searching) && (
                <ul className="birth-places" role="listbox">
                  {searching && places.length === 0 && <li className="birth-place-status">{text.searching}</li>}
                  {places.map((place) => (
                    <li key={`${place.label}-${place.latitude}`}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedPlace(place);
                          setForm((current) => ({ ...current, placeQuery: place.label }));
                          setPlaceOpen(false);
                          setError('');
                        }}
                      >
                        {place.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <fieldset className="birth-date">
              <legend>{text.date}</legend>
              <select value={form.month} onChange={(event) => setField('month', event.target.value)} aria-label={text.month}>
                <option value="">{text.month}</option>
                {copy.months.map((month, index) => (
                  <option key={month} value={index + 1}>{month}</option>
                ))}
              </select>
              <input
                inputMode="numeric"
                placeholder={text.day}
                aria-label={text.day}
                value={form.day}
                onChange={(event) => setField('day', event.target.value.replace(/\D/g, '').slice(0, 2))}
              />
              <input
                inputMode="numeric"
                placeholder={text.year}
                aria-label={text.year}
                value={form.year}
                onChange={(event) => setField('year', event.target.value.replace(/\D/g, '').slice(0, 4))}
              />
            </fieldset>
            <fieldset className="birth-time" disabled={form.timeUnknown}>
              <legend>{text.time}</legend>
              <input
                inputMode="numeric"
                placeholder={text.hour}
                aria-label={text.hour}
                value={form.hour}
                onChange={(event) => setField('hour', event.target.value.replace(/\D/g, '').slice(0, 2))}
              />
              <input
                inputMode="numeric"
                placeholder={text.minute}
                aria-label={text.minuteLabel}
                value={form.minute}
                onChange={(event) => setField('minute', event.target.value.replace(/\D/g, '').slice(0, 2))}
              />
              <select value={form.ampm} aria-label="AM or PM" onChange={(event) => setField('ampm', event.target.value)}>
                <option>AM</option>
                <option>PM</option>
              </select>
            </fieldset>
            <label className="birth-check">
              <input
                type="checkbox"
                checked={form.timeUnknown}
                onChange={(event) => setField('timeUnknown', event.target.checked)}
              />
              {text.unknownTime}
            </label>
            {text.timeNote && <p className="birth-time-note">{text.timeNote}</p>}
            <label className="birth-language">
              {text.readingIn}
              <select
                value={language}
                onChange={(event) => chooseLanguage(event.target.value)}
                aria-label={text.readingLanguage}
              >
                {READING_LANGUAGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value} lang={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {error && <p className="birth-error">{error}</p>}
            <button className="birth-draw" type="submit" disabled={phase === 'drawing'}>
              {intro.button}
            </button>
          </form>
        </>
      ) : (
        <div className="birth-result" {...direction}>
          <div className="birth-summary-row">
            <p className="birth-summary">{chart?.summary || drawn.drawing}</p>
            <button type="button" className="birth-text" onClick={handleEdit}>{drawn.edit}</button>
          </div>
          {chart && <ChartNav items={navItems} />}
          {!chart && <p className="birth-wait">{drawn.drawingWheel}</p>}
          {chart?.time_unknown && (
            <p className="birth-note">
              {drawn.timeUnknown}
            </p>
          )}
          {chart && (
            <section id="chart-big-three" className="birth-section" aria-labelledby="chart-big-three-title">
              <h2 id="chart-big-three-title">{drawn.bigThree}</h2>
              <BigThree
                placements={chart.placements}
                timeUnknown={chart.time_unknown}
                moonChange={chart.moon_change}
                variant={variant}
              />
            </section>
          )}
          {chart && (
            <section id="chart-reading" className="birth-section" aria-labelledby="chart-reading-title">
            <h2 id="chart-reading-title">{drawn.chartReading}</h2>
            <div className="birth-read">
              <div className="birth-figure">
                <div className="birth-wheel" dangerouslySetInnerHTML={{ __html: chart.svg }} />
                {!chart.time_unknown && (
                  <div className="birth-houses" role="group" aria-label={drawn.houseSystem}>
                    {houses.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={chart.house_system === item.id ? 'active' : ''}
                        disabled={houseBusy}
                        onClick={() => handleHouse(item.id)}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
                {!chart.time_unknown && reading && readingHouse && chart.house_system !== readingHouse && (
                  <p className="birth-note">
                    {fill(drawn.readingUses, { houses: houses.find((item) => item.id === readingHouse)?.label })}{' '}
                    <button type="button" className="birth-text" onClick={retryReading}>{drawn.readAgainHouses}</button>
                  </p>
                )}
                <button type="button" className="birth-details-open" onClick={openDetails}>
                  {drawn.chartDetails}
                </button>
              </div>
              {reading && wrapReading(reading, language)}
              {!reading && !readingError && <p className="birth-wait">{drawn.readingChart}</p>}
            </div>
            </section>
          )}
          {readingError && (
            <p className="birth-error">
              {readingError}{' '}
              <button type="button" className="birth-text" onClick={retryReading}>{drawn.tryReading}</button>
            </p>
          )}
          {session && (
            <section id="chart-ask" className="birth-ask birth-section" aria-labelledby="birth-ask-title">
              <h3 id="birth-ask-title">{drawn.askTitle}</h3>
              <p className="birth-note">
                {drawn.askNote}
              </p>
              {answers.map((item, index) => (
                <div className="birth-qa" key={index}>
                  <p className="birth-qa-question">{item.question}</p>
                  {item.answer && wrapReading(item.answer, language)}
                  {!item.answer && !item.error && <p className="birth-wait">{drawn.looking}</p>}
                  {item.error && <p className="birth-error">{item.error}</p>}
                </div>
              ))}
              <form className="birth-ask-form" onSubmit={handleAsk}>
                <textarea
                  value={question}
                  rows={3}
                  maxLength={500}
                  aria-label={drawn.askLabel}
                  placeholder={drawn.askPlaceholder}
                  onChange={(event) => setQuestion(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) handleAsk(event);
                  }}
                />
                <button className="birth-draw" type="submit" disabled={asking || !question.trim()}>
                  {asking ? drawn.answering : drawn.ask}
                </button>
              </form>
            </section>
          )}
          {chart && (
            <section id="chart-tarot" className="birth-section chart-tarot" aria-labelledby="chart-tarot-title">
              <h2 id="chart-tarot-title">{drawn.tarotTitle}</h2>
              <ChartTarot
                key={session || 'waiting'}
                session={reading && !readingError ? session : null}
                language={language}
                timeUnknown={chart.time_unknown}
                onDrawn={() => setTarotDrawn(true)}
              />
            </section>
          )}
          {chart && (
            <section id="chart-share" className="birth-section" aria-labelledby="chart-share-title">
              <h2 id="chart-share-title">{drawn.shareTitle}</h2>
              <ChartShare
                session={reading && !readingError ? session : null}
                hasAnswers={answers.some((item) => item.answer)}
                hasTarot={tarotDrawn}
              />
            </section>
          )}
          {chart && (
            <section id="chart-compare" className="birth-section birth-compare" aria-labelledby="chart-compare-title">
              <h2 id="chart-compare-title">{drawn.compareTitle}</h2>
              <p>
                {drawn.compareText}
              </p>
              <Link
                className="birth-draw"
                to={chartHref('/birth-chart-compatibility/', language, session ? { you: session } : null)}
              >
                {drawn.compareCta}
              </Link>
              <p className="birth-note">
                <LinkedNote
                  template={drawn.signsNote}
                  links={{ zodiac: { href: '/zodiac-compatibility/', label: drawn.zodiacLink } }}
                />
              </p>
            </section>
          )}
          {chart && (
            <section id="chart-today" className="birth-section birth-compare" aria-labelledby="chart-today-title">
              <h2 id="chart-today-title">{drawn.todayTitle || 'See your astrology today'}</h2>
              <p>
                {drawn.todayText || 'Today’s planets, compared with this chart. Your birth details stay in a cookie on this device, so the daily reading can open without asking again.'}
              </p>
              <Link className="birth-draw" to={chartHref('/personalized-daily-horoscope/', language, session ? { chart: session } : null)}>
                {drawn.todayCta || 'See your astrology today'}
              </Link>
            </section>
          )}
          {phase === 'drawn' && (
            <button type="button" className="birth-again" onClick={handleReset}>{drawn.drawAnother}</button>
          )}
          {chart && (
            <dialog
              ref={detailsRef}
              className="birth-details"
              aria-labelledby="birth-details-title"
              onClick={(event) => {
                if (event.target === detailsRef.current) closeDetails();
              }}
            >
              <div className="birth-details-inner">
                <div className="birth-details-head">
                  <h3 id="birth-details-title">{drawn.chartDetails}</h3>
                  <button type="button" className="birth-details-close" aria-label={drawn.close} onClick={closeDetails}>×</button>
                </div>
                {chart.house_system_label && <p className="birth-note">{fill(drawn.housesTropical, { system: chart.house_system_label })}</p>}
                <ul className="birth-placements">
                  {chart.placements.map((placement) => (
                    <li key={placement.label}>
                      <span>{placement.label}</span>
                      <span>
                        {placement.degree} {placement.sign}
                        {placement.house ? `, ${drawn.houseWord} ${placement.house}` : ''}
                        {placement.retrograde ? `, ${drawn.retrograde}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
                {chart.patterns?.length > 0 && (
                  <>
                    <h4>{drawn.patterns}</h4>
                    <ul className="birth-patterns">
                      {chart.patterns.map((pattern) => <li key={pattern}>{pattern}</li>)}
                    </ul>
                  </>
                )}
              </div>
            </dialog>
          )}
        </div>
      )}
    </div>
  );
};

export default BirthChartTool;
